import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BoardField } from '../board-v3.types';
import {
  getCustomFieldMaxLength,
  normalizeCustomFieldValue,
  sanitizePhoneInputValue,
} from '../custom-field.validators';
import { CurrencyOption, getCurrencyOptions, isPriceFieldValue, normalizePriceAmount } from '../price-field.utils';

@Component({
  selector: 'app-custom-field-control',
  templateUrl: './custom-field-control.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomFieldControlComponent implements OnChanges {
  @Input({ required: true }) field!: BoardField;
  @Input({ required: true }) control!: FormControl;
  @Input() dateTimeControl: FormControl<string> | FormControl<unknown> | null = null;
  @Input() appearance: 'quick' | 'detail' = 'quick';
  @Input() showLabel = true;

  @Output() blurred = new EventEmitter<void>();
  @Output() dateChanged = new EventEmitter<void>();
  @Output() selectChanged = new EventEmitter<void>();

  readonly currencyOptions: CurrencyOption[] = getCurrencyOptions();
  readonly priceAmountControl = new FormControl<string>('', { nonNullable: true });
  readonly priceCurrencyControl = new FormControl<string>('UAH', { nonNullable: true });
  private keepPriceFocusUntil = 0;
  private keepDateFocusUntil = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] || changes['control']) {
      this.syncPriceControls();
    }
  }

  get isDetail(): boolean {
    return this.appearance === 'detail';
  }

  get datePickerInputClass(): string {
    const baseClass = this.isDetail
      ? 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400'
      : 'h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400';

    return this.control.invalid && this.control.touched ? `${baseClass} border-red-400` : baseClass;
  }

  get timePickerInputClass(): string {
    return this.isDetail
      ? 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400 disabled:cursor-not-allowed disabled:bg-slate-50'
      : 'h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-gray-50';
  }

  get options(): { label: string; value: string }[] {
    return (this.field.options ?? []).map((value) => ({ label: value, value }));
  }

  get inputType(): string {
    if (this.field.input_type === 'phone') return 'tel';
    if (this.field.input_type === 'email') return 'email';
    if (this.field.input_type === 'link') return 'url';
    return 'text';
  }

  get inputMode(): string | null {
    if (this.field.input_type === 'phone') return 'tel';
    if (this.field.input_type === 'email') return 'email';
    if (this.field.input_type === 'link') return 'url';
    return ['number', 'price'].includes(this.field.input_type) ? 'decimal' : null;
  }

  get maxLength(): number {
    return getCustomFieldMaxLength(this.field.input_type);
  }

  get shouldShowCharacterLimit(): boolean {
    return ['text', 'textarea'].includes(this.field.input_type);
  }

  get valueLength(): number {
    const value = this.control.value;
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string') return value.length;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value).length;
    return 0;
  }

  get errorKey(): string {
    const errors = this.control.errors;
    if (errors?.['maxlength']) return 'input_errors.maxlength';
    if (errors?.['singleLine']) return 'input_errors.single_line';
    if (errors?.['number']) return 'input_errors.number';
    if (errors?.['price']) return 'input_errors.price';
    if (errors?.['currency']) return 'input_errors.currency';
    if (errors?.['phone']) return 'input_errors.phone';
    if (errors?.['email']) return 'input_errors.email';
    if (errors?.['link']) return 'input_errors.link';
    return 'input_errors.required';
  }

  onFocusOut(event: FocusEvent, container: HTMLElement): void {
    // The select panel is appended to <body>, so moving focus to it must not
    // be treated as leaving this field.
    if (this.field.input_type === 'select') return;

    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && container.contains(nextTarget)) return;

    if (this.field.input_type === 'date' && this.shouldKeepDateFocus()) return;

    if (this.field.input_type === 'price') {
      if (this.shouldKeepPriceFocus()) return;
      this.commitPriceValue();
      this.control.markAsTouched();
      this.control.updateValueAndValidity();
      this.blurred.emit();
      return;
    }

    const normalized = normalizeCustomFieldValue(this.field.input_type, this.control.value);
    if (normalized !== this.control.value) this.control.setValue(normalized);
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
    this.blurred.emit();
  }

  onPriceInput(event: Event): void {
    if (this.field.input_type !== 'price') return;
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^\d.,\s]/g, '');
    if (value === input.value) return;
    input.value = value;
    this.priceAmountControl.setValue(value);
    this.commitPriceValue(true);
  }

  onPhoneInput(event: Event): void {
    if (this.field.input_type !== 'phone') return;
    const input = event.target as HTMLInputElement;
    const value = sanitizePhoneInputValue(input.value);
    if (value === input.value) return;
    input.value = value;
    this.control.setValue(value);
  }

  onPriceAmountChange(): void {
    this.commitPriceValue(true);
  }

  onPriceCurrencyChange(): void {
    this.keepPriceFocusForSelect();
    this.commitPriceValue(true);
    this.selectChanged.emit();
  }

  keepPriceFocusForSelect(): void {
    this.keepPriceFocusUntil = Date.now() + 300;
  }

  keepDateFocusForPicker(): void {
    this.keepDateFocusUntil = Date.now() + 300;
  }

  onDateChange(): void {
    if (!this.control.value && this.dateTimeControl?.value) this.dateTimeControl.setValue('');
    this.dateChanged.emit();
  }

  onSelectChange(): void {
    this.control.markAsTouched();
    this.selectChanged.emit();
  }

  private syncPriceControls(): void {
    if (this.field?.input_type !== 'price') return;

    const value = this.control?.value;
    const defaultValue = this.field.default_value;
    const currency = isPriceFieldValue(value)
      ? value.currency
      : isPriceFieldValue(defaultValue)
        ? defaultValue.currency
        : 'UAH';
    const amount = isPriceFieldValue(value) && value.amount !== null ? String(value.amount) : '';

    this.priceAmountControl.setValue(amount, { emitEvent: false });
    this.priceCurrencyControl.setValue(currency, { emitEvent: false });
  }

  private commitPriceValue(markDirty = false): void {
    const rawAmount = this.priceAmountControl.value;
    const normalizedAmount = normalizePriceAmount(rawAmount);
    this.control.setValue({
      amount: rawAmount.trim() ? (normalizedAmount ?? rawAmount) : null,
      currency: this.priceCurrencyControl.value || 'UAH',
    });
    if (markDirty) this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }

  private shouldKeepPriceFocus(): boolean {
    if (Date.now() < this.keepPriceFocusUntil) return true;
    const activeElement = document.activeElement;
    if (
      activeElement instanceof Element &&
      activeElement.closest(
        '.p-select, .p-select-overlay, .p-autocomplete-overlay, .p-dropdown-panel, .p-multiselect-panel',
      )
    ) {
      return true;
    }
    return !!document.querySelector(
      '.p-select-overlay, .p-autocomplete-overlay, .p-dropdown-panel, .p-multiselect-panel',
    );
  }

  private shouldKeepDateFocus(): boolean {
    if (Date.now() < this.keepDateFocusUntil) return true;
    const activeElement = document.activeElement;
    if (
      activeElement instanceof Element &&
      activeElement.closest('.p-datepicker, .p-datepicker-panel, .board-v3-datepicker-panel')
    ) {
      return true;
    }
    return !!document.querySelector('.p-datepicker-panel, .board-v3-datepicker-panel');
  }
}
